import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useTransporterInfo } from "../hooks/useTransporterInfo";
import { SubscriptionType } from "../interfaces/transporters.interface";
import ChapterService from "../service/ChapterService";
import { Chapter } from "../interfaces/chapters.interface";
import LoadingSpinner from "../components/chapters/LoadingSpinner";
import PremiumFeatureNotice from "../components/chapters/PremiumFeatureNotice";
import ChaptersList from "../components/chapters/ChaptersList";

const ChaptersManagement: React.FC = () => {
  const history = useHistory();
  const { transporterInfo } = useTransporterInfo();
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const chapterService = useMemo(() => new ChapterService(), []);
  const loadChapters = useCallback(async () => {
    try {
      const chaptersData = await chapterService.getChapters();
      setChapters(chaptersData);
      setIsLoading(false);
    } catch (err) {
      console.error("Error loading chapters:", err);
      setIsLoading(false);
    }
  }, [chapterService]);

  useEffect(() => {
    if (transporterInfo.subscriptionType === SubscriptionType.PAID) {
      loadChapters();
    } else {
      setIsLoading(false);
    }
  }, [loadChapters, transporterInfo.subscriptionType]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (transporterInfo.subscriptionType !== SubscriptionType.PAID) {
    return <PremiumFeatureNotice />;
  }

  // Ensure transporterInfo._id exists before rendering ChaptersList
  if (!transporterInfo._id) {
    return null;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-70px)] bg-slate-50">
      <Toast />
      <ConfirmDialog />
      <div className="px-12 flex flex-col h-full">
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold">Gestion des Chapitres</h1>
          <button
            onClick={() => history.push("/chapters/create")}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <i className="ph-bold ph-plus mr-2"></i>
            Nouveau Chapitre
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto pr-4 -mr-4">
            <div className="space-y-6 pb-6">
              <ChaptersList 
                chapters={chapters} 
                transporterId={transporterInfo._id}
                onChapterDeleted={loadChapters}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChaptersManagement;
